import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const checks = [
  { label: 'Zálohovanie dát', done: true },
  { label: 'Aktualizácia systému', done: true },
  { label: 'Optimalizácia databázy', done: false },
  { label: 'Testovanie funkcionality', done: false },
  { label: 'Nasadenie zmien', done: false },
];

/* ── Animated dots ── */
function AnimatedDots() {
  const [dots, setDots] = useState('');
  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? '' : prev + '.'));
    }, 500);
    return () => clearInterval(interval);
  }, []);
  return <span className="inline-block w-6 text-left">{dots}</span>;
}

/* ── Progress bar ── */
function ProgressBar() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 42) { clearInterval(interval); return 42; }
          return prev + 1;
        });
      }, 30);
      return () => clearInterval(interval);
    }, 600);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-2">
        <span className="text-zinc-500 text-xs font-medium">Priebeh údržby</span>
        <span className="text-orange-400 text-xs font-bold">{progress}%</span>
      </div>
      <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{
            width: `${progress}%`,
            background: 'linear-gradient(90deg, #f97316 0%, #fbbf24 100%)',
            boxShadow: '0 0 8px rgba(249,115,22,0.5)',
          }}
        />
      </div>
    </div>
  );
}

/* ── Canvas particle field ── */
function ParticleCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animId: number;
    let W = 0, H = 0;
    type P = { x: number; y: number; vx: number; vy: number; r: number; alpha: number; alphaDir: number };
    let particles: P[] = [];
    const resize = () => { W = canvas.width = canvas.offsetWidth; H = canvas.height = canvas.offsetHeight; };
    const init = () => {
      particles = Array.from({ length: 60 }, () => ({
        x: Math.random() * W, y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.25, vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.4 + 0.4, alpha: Math.random() * 0.45 + 0.08,
        alphaDir: Math.random() > 0.5 ? 1 : -1,
      }));
    };
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 110) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(249,115,22,${0.055 * (1 - dist / 110)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
      particles.forEach((p) => {
        p.x += p.vx; p.y += p.vy;
        p.alpha += p.alphaDir * 0.003;
        if (p.alpha > 0.55 || p.alpha < 0.05) p.alphaDir *= -1;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(249,115,22,${p.alpha})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    resize(); init(); draw();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => { cancelAnimationFrame(animId); ro.disconnect(); };
  }, []);
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />;
}

function GridBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(249,115,22,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(249,115,22,0.05) 1px, transparent 1px)`,
          backgroundSize: '64px 64px',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-transparent to-zinc-950" />
      <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-transparent to-zinc-950" />
    </div>
  );
}

function NoiseOverlay() {
  return (
    <div
      className="absolute inset-0 pointer-events-none opacity-[0.022]"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
        backgroundSize: '128px 128px',
      }}
    />
  );
}

function Orbs() {
  return (
    <>
      <div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: '600px', height: '600px', top: '50%', left: '50%',
          background: 'radial-gradient(circle, rgba(249,115,22,0.06) 0%, transparent 65%)',
          animation: 'orbPulse 12s ease-in-out infinite',
        }}
      />
      <div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: '300px', height: '300px', top: '8%', right: '6%',
          background: 'radial-gradient(circle, rgba(249,115,22,0.09) 0%, transparent 65%)',
          animation: 'orbDrift 9s ease-in-out infinite',
        }}
      />
      <div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: '220px', height: '220px', bottom: '12%', left: '4%',
          background: 'radial-gradient(circle, rgba(249,115,22,0.07) 0%, transparent 65%)',
          animation: 'orbDrift 13s ease-in-out infinite reverse',
        }}
      />
    </>
  );
}

function Rings() {
  const rings = [
    { size: 420, speed: '30s', opacity: 0.06, dash: true },
    { size: 620, speed: '50s', opacity: 0.035, dash: false },
    { size: 820, speed: '75s', opacity: 0.02, dash: true },
  ];
  return (
    <>
      {rings.map((ring, i) => (
        <div
          key={i}
          className="absolute pointer-events-none rounded-full"
          style={{
            width: ring.size, height: ring.size,
            top: '50%', left: '50%',
            marginTop: -ring.size / 2, marginLeft: -ring.size / 2,
            border: `1px ${ring.dash ? 'dashed' : 'solid'} rgba(249,115,22,0.6)`,
            opacity: ring.opacity,
            animation: `rotateSlow ${ring.speed} linear infinite ${i % 2 === 1 ? 'reverse' : ''}`,
          }}
        />
      ))}
    </>
  );
}

function ScanLine() {
  return (
    <div
      className="absolute left-0 right-0 h-[2px] pointer-events-none"
      style={{
        background: 'linear-gradient(to right, transparent, rgba(249,115,22,0.35) 30%, rgba(249,115,22,0.55) 50%, rgba(249,115,22,0.35) 70%, transparent)',
        animation: 'scanLine 10s linear infinite',
        filter: 'blur(1px)',
      }}
    />
  );
}

function Sparks() {
  const sparks = [
    { top: '7%', left: '5%', size: 2, delay: '0s', dur: '5s' },
    { top: '20%', left: '92%', size: 1.5, delay: '1.2s', dur: '4s' },
    { top: '58%', left: '2%', size: 2.5, delay: '0.8s', dur: '6s' },
    { top: '80%', left: '95%', size: 1.5, delay: '2s', dur: '5s' },
    { top: '38%', left: '98%', size: 2, delay: '0.5s', dur: '7s' },
    { top: '90%', left: '10%', size: 1, delay: '1.8s', dur: '4.5s' },
  ];
  return (
    <>
      {sparks.map((s, i) => (
        <div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            top: s.top, left: s.left,
            width: s.size, height: s.size,
            background: 'rgba(249,115,22,0.7)',
            boxShadow: '0 0 4px rgba(249,115,22,0.8)',
            animation: `sparkFloat ${s.dur} ease-in-out infinite`,
            animationDelay: s.delay,
          }}
        />
      ))}
    </>
  );
}

/* ── Pulsing tool icon ── */
function ToolIcon() {
  return (
    <div className="flex justify-center mb-8">
      <div className="relative">
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: 'rgba(249,115,22,0.12)',
            animation: 'pulseRing 2.8s ease-out infinite',
          }}
        />
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: 'rgba(249,115,22,0.07)',
            animation: 'pulseRing 2.8s ease-out infinite',
            animationDelay: '0.9s',
          }}
        />
        <div
          className="relative w-20 h-20 flex items-center justify-center rounded-full"
          style={{
            background: 'linear-gradient(145deg, #1c1c1e 0%, #111113 100%)',
            border: '1px solid rgba(249,115,22,0.2)',
            boxShadow: '0 0 30px rgba(249,115,22,0.12)',
          }}
        >
          <i className="ri-tools-line text-orange-400 text-3xl" />
        </div>
      </div>
    </div>
  );
}

export default function UdrzbaPage() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700;800&display=swap');
        @keyframes orbPulse { 0%,100%{transform:translate(-50%,-50%) scale(1);opacity:.8} 50%{transform:translate(-50%,-50%) scale(1.1);opacity:1} }
        @keyframes orbDrift { 0%,100%{transform:scale(1) translateY(0px)} 50%{transform:scale(1.15) translateY(-20px)} }
        @keyframes rotateSlow { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes scanLine { 0%{top:-4px;opacity:0} 3%{opacity:1} 97%{opacity:1} 100%{top:100%;opacity:0} }
        @keyframes sparkFloat { 0%,100%{transform:translateY(0px) scale(1);opacity:.5} 33%{transform:translateY(-12px) scale(1.4);opacity:1} 66%{transform:translateY(-5px) scale(.8);opacity:.7} }
        @keyframes pulseRing { 0%{transform:scale(1);opacity:.5} 100%{transform:scale(2.4);opacity:0} }
        @keyframes fadeSlideUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes borderGlow { 0%,100%{border-color:rgba(249,115,22,0.12)} 50%{border-color:rgba(249,115,22,0.35)} }
        .font-space-udrzba { font-family: 'Space Grotesk', sans-serif; }
      `}</style>

      <div className="font-space-udrzba min-h-screen bg-zinc-950 flex flex-col items-center justify-center px-6 py-16 relative overflow-hidden">

        <GridBackground />
        <NoiseOverlay />
        <ParticleCanvas />
        <Orbs />
        <Rings />
        <ScanLine />
        <Sparks />

        {/* Edge lines */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-500/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-500/15 to-transparent" />

        {/* Corner accents */}
        <div className="absolute top-6 left-6 w-8 h-8 border-t border-l border-orange-500/25 pointer-events-none" />
        <div className="absolute top-6 right-6 w-8 h-8 border-t border-r border-orange-500/25 pointer-events-none" />
        <div className="absolute bottom-6 left-6 w-8 h-8 border-b border-l border-orange-500/25 pointer-events-none" />
        <div className="absolute bottom-6 right-6 w-8 h-8 border-b border-r border-orange-500/25 pointer-events-none" />

        {/* Logo */}
        <div
          className="mb-12 relative z-10"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(-16px)',
            transition: 'all 0.7s cubic-bezier(0.4,0,0.2,1)',
          }}
        >
          <Link to="/" className="flex items-center gap-2.5 cursor-pointer group">
            <div
              className="w-10 h-10 flex items-center justify-center bg-orange-500 rounded-xl group-hover:bg-orange-400 transition-all duration-200 group-hover:scale-105"
              style={{ boxShadow: '0 0 20px rgba(249,115,22,0.4)' }}
            >
              <i className="ri-rocket-2-fill text-white text-base" />
            </div>
            <span className="text-white font-bold text-2xl tracking-tight">
              boost<span className="text-orange-500">io</span>
            </span>
          </Link>
        </div>

        {/* Main card */}
        <div
          className="w-full max-w-lg relative z-10"
          style={{
            opacity: visible ? 1 : 0,
            animation: visible ? 'fadeSlideUp 0.7s cubic-bezier(0.4,0,0.2,1) 0.1s both' : 'none',
          }}
        >
          {/* Pulsing tool icon */}
          <ToolIcon />

          {/* Badge + heading */}
          <div className="text-center mb-8">
            <div
              className="inline-flex items-center gap-2 border text-orange-400 text-xs font-semibold px-4 py-2 rounded-full mb-5"
              style={{
                background: 'rgba(249,115,22,0.08)',
                borderColor: 'rgba(249,115,22,0.2)',
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full bg-orange-400"
                style={{ animation: 'sparkFloat 2s ease-in-out infinite' }}
              />
              Prebieha údržba
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4 leading-tight tracking-tight">
              Stránka je dočasne<br />
              <span
                style={{
                  background: 'linear-gradient(90deg, #fb923c 0%, #fde68a 40%, #f97316 70%, #fb923c 100%)',
                  backgroundSize: '200% auto',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                nedostupná
              </span>
            </h1>
            <p className="text-zinc-400 text-base leading-relaxed">
              Pracujeme na vylepšeniach, aby sme vám mohli ponúknuť ešte lepší zážitok.
              Vráťte sa o chvíľu<AnimatedDots />
            </p>
          </div>

          {/* Progress card */}
          <div
            className="rounded-2xl p-6 mb-4"
            style={{
              background: 'linear-gradient(145deg, rgba(28,28,30,0.9) 0%, rgba(17,17,19,0.9) 100%)',
              border: '1px solid rgba(249,115,22,0.12)',
              backdropFilter: 'blur(12px)',
              animation: 'borderGlow 4s ease-in-out infinite',
            }}
          >
            <ProgressBar />

            <div className="mt-5 space-y-3">
              {checks.map((item) => (
                <div key={item.label} className="flex items-center gap-3">
                  <div
                    className="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0"
                    style={{
                      background: item.done ? 'rgba(16,185,129,0.12)' : 'rgba(39,39,42,0.8)',
                      border: item.done ? '1px solid rgba(16,185,129,0.3)' : '1px solid rgba(63,63,70,0.8)',
                    }}
                  >
                    {item.done ? (
                      <i className="ri-check-line text-emerald-400 text-xs" />
                    ) : (
                      <div className="w-1.5 h-1.5 rounded-full bg-zinc-600" />
                    )}
                  </div>
                  <span className={`text-sm ${item.done ? 'text-zinc-300' : 'text-zinc-600'}`}>
                    {item.label}
                  </span>
                  {item.done && (
                    <span className="ml-auto text-emerald-500 text-xs font-semibold">Hotovo</span>
                  )}
                  {!item.done && (
                    <span className="ml-auto text-zinc-700 text-xs">Čaká</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Info box */}
          <div
            className="rounded-xl p-4 flex items-start gap-3 mb-8"
            style={{
              background: 'rgba(17,17,19,0.7)',
              border: '1px solid rgba(63,63,70,0.5)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
              <i className="ri-information-line text-zinc-500 text-lg" />
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Ak máte urgentný dopyt, napíšte nám priamo na{' '}
              <a
                href="mailto:hello@boostio.sk"
                className="text-orange-400 hover:text-orange-300 font-medium transition-colors duration-200"
              >
                hello@boostio.sk
              </a>
              . Ozveme sa čo najskôr.
            </p>
          </div>

          {/* Contact links */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <a
              href="mailto:hello@boostio.sk"
              className="flex items-center gap-2 text-zinc-500 hover:text-orange-400 text-sm transition-all duration-200 cursor-pointer group"
            >
              <div
                className="w-7 h-7 flex items-center justify-center rounded-lg transition-colors duration-200"
                style={{ background: 'rgba(28,28,30,0.9)', border: '1px solid rgba(63,63,70,0.6)' }}
              >
                <i className="ri-mail-line text-xs" />
              </div>
              hello@boostio.sk
            </a>
            <div className="w-px h-4 bg-zinc-800 hidden sm:block" />
            <a
              href="https://wa.me/421905929167"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-zinc-500 hover:text-orange-400 text-sm transition-all duration-200 cursor-pointer group"
            >
              <div
                className="w-7 h-7 flex items-center justify-center rounded-lg transition-colors duration-200"
                style={{ background: 'rgba(28,28,30,0.9)', border: '1px solid rgba(63,63,70,0.6)' }}
              >
                <i className="ri-whatsapp-line text-xs" />
              </div>
              +421 905 929 167
            </a>
          </div>

          {/* Footer note */}
          <p className="text-center text-zinc-700 text-xs">
            © {new Date().getFullYear()} Boostio · Kamil Frajtko
          </p>
        </div>
      </div>
    </>
  );
}
